// pages/CertificacionesGlobal.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import '../styles/CertificacionesGlobal.css';
import { useFiscal } from '../hooks/useFiscal';

const CertificacionesGlobal = () => {
  const fiscal = useFiscal();
  const navigate = useNavigate();
  
  const [certificaciones, setCertificaciones] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filtroProyecto, setFiltroProyecto] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  
  // Modal para nueva certificación
  const [mostrarSelectorProyecto, setMostrarSelectorProyecto] = useState(false);

  useEffect(() => {
    cargarDatos();
  }, [filtroProyecto, filtroEstado]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      
      // Construir query params
      const params = {};
      if (filtroProyecto) params.proyectoId = filtroProyecto;
      if (filtroEstado) params.estado = filtroEstado;
      
      const [resCert, resProyectos] = await Promise.all([
        api.get('/certificaciones', { params }),
        api.get('/proyectos')
      ]);
      
      setCertificaciones(resCert.data.certificaciones);
      setResumen(resCert.data.resumen);
    const proyectosData = Array.isArray(resProyectos.data) 
  ? resProyectos.data 
  : resProyectos.data.proyectos || [];

setProyectos(proyectosData.filter(p => p.estado === 'activo'));
      
    } catch (error) {
      console.error('Error cargando datos:', error);
      alert('Error al cargar certificaciones');
    } finally {
      setLoading(false);
    }
  };

  const formatearMoneda = (valor) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: fiscal.moneda?.codigo || 'DOP'
    }).format(valor || 0);
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(String(fecha).substring(0,10) + 'T00:00:00').toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBadgeClass = (estado) => {
    const classes = {
      'borrador': 'badge-borrador',
      'revisión': 'badge-revision',
      'aprobada': 'badge-aprobada',
      'facturada': 'badge-facturada'
    };
    return classes[estado] || 'badge-borrador';
  };

  const getBadgeIcon = (estado) => {
    const icons = {
      'borrador': '📝',
      'revisión': '🔍',
      'aprobada': '✅',
      'facturada': '💰'
    };
    return icons[estado] || '📝';
  };

  const handleNuevaCertificacion = () => {
    setMostrarSelectorProyecto(true);
  };

  const handleSeleccionarProyecto = (proyectoId) => {
    setMostrarSelectorProyecto(false);
    navigate(`/proyectos/${proyectoId}/certificaciones/nueva`);
  };

 const handleVerDetalle = (cert) => {
  navigate(`/certificaciones/${cert.id}`);
};

  const limpiarFiltros = () => {
    setFiltroProyecto('');
    setFiltroEstado('');
  };

  if (loading) {
    return (
      <div className="cert-global-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Cargando certificaciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cert-global-container">
      {/* Header */}
      <div className="cert-global-header">
        <div className="header-info">
          <h1>📜 Certificaciones de Obra</h1>
          <p className="subtitle">Vista global de todas las certificaciones</p>
        </div>
        <button 
          onClick={handleNuevaCertificacion}
          className="btn-primary"
        >
          ➕ Nueva Certificación
        </button>
      </div>

      {/* Resumen Global */}
      {resumen && (
        <div className="resumen-grid">
          <div className="resumen-card certificado">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <span className="card-label">Total Certificado</span>
              <span className="card-value">{formatearMoneda(resumen.monto_total_certificado)}</span>
            </div>
          </div>

          <div className="resumen-card total">
            <div className="card-icon">📊</div>
            <div className="card-content">
              <span className="card-label">Total Certificaciones</span>
              <span className="card-value-number">{resumen.total}</span>
            </div>
          </div>

          <div className="resumen-card estados">
            <div className="card-icon">📋</div>
            <div className="card-content">
              <span className="card-label">Por Estado</span>
              <div className="estados-mini">
                <span className="estado-count">📝 {resumen.borradores}</span>
                <span className="estado-count">🔍 {resumen.revision}</span>
                <span className="estado-count">✅ {resumen.aprobadas}</span>
                <span className="estado-count">💰 {resumen.facturadas}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="filtros-container">
        <select
          value={filtroProyecto}
          onChange={(e) => setFiltroProyecto(e.target.value)}
          className="select-filtro"
        >
          <option value="">Todos los Proyectos</option>
          {proyectos.map(p => (
            <option key={p.id} value={p.id}>{p.nombre}</option>
          ))}
        </select>

        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          className="select-filtro"
        >
          <option value="">Todos los Estados</option>
          <option value="borrador">Borradores</option>
          <option value="revisión">En Revisión</option>
          <option value="aprobada">Aprobadas</option>
          <option value="facturada">Facturadas</option>
        </select>

        {(filtroProyecto || filtroEstado) && (
          <button onClick={limpiarFiltros} className="btn-limpiar">
            🗑️ Limpiar Filtros
          </button>
        )}
      </div>

      {/* Lista de Certificaciones */}
      <div className="certificaciones-lista">
        {certificaciones.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>No hay certificaciones</h3>
            <p>Comienza creando tu primera certificación de obra</p>
            <button onClick={handleNuevaCertificacion} className="btn-primary">
              ➕ Crear Primera Certificación
            </button>
          </div>
        ) : (
          <div className="cert-grid">
            {certificaciones.map(cert => (
              <div key={cert.id} className="cert-card-global">
                <div className="cert-card-header-global">
                  <div className="proyecto-badge">
                    🏗️ {cert.proyecto?.nombre || 'Sin proyecto'}
                  </div>
                  <span className={`badge ${getBadgeClass(cert.estado)}`}>
                    {getBadgeIcon(cert.estado)} {cert.estado}
                  </span>
                </div>

                <div className="cert-card-body-global">
                  <div className="cert-numero-global">
                    Certificación #{cert.numero_certificacion}
                  </div>

                  <div className="cert-info-grid">
                    <div className="info-item-global">
                      <span className="info-icon">📅</span>
                      <div>
                        <span className="info-label-global">Fecha</span>
                        <span className="info-value-global">{formatearFecha(cert.fecha)}</span>
                      </div>
                    </div>

                    {cert.periodo_inicio && cert.periodo_fin && (
                      <div className="info-item-global">
                        <span className="info-icon">📆</span>
                        <div>
                          <span className="info-label-global">Período</span>
                          <span className="info-value-global">
                            {formatearFecha(cert.periodo_inicio)} - {formatearFecha(cert.periodo_fin)}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="info-item-global">
                      <span className="info-icon">📦</span>
                      <div>
                        <span className="info-label-global">Partidas</span>
                        <span className="info-value-global">{cert.partidas?.length || 0}</span>
                      </div>
                    </div>
                  </div>

                  <div className="cert-monto-global">
                    <span className="monto-label-global">Monto Certificado</span>
                    <span className="monto-value-global">{formatearMoneda(cert.monto_total)}</span>
                  </div>

                  {cert.factura && (
                    <div className="cert-factura-global">
                      💰 Factura: {cert.factura.numero_factura} - {cert.factura.ncf}
                    </div>
                  )}
                </div>

                <div className="cert-card-footer-global">
                 <button
                 onClick={(e) => {
                   e.stopPropagation();
                        handleVerDetalle(cert);
                          }}
                         className="btn-secondary"
                         >
                     👁️ Ver Detalle
                    </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Selector de Proyecto */}
      {mostrarSelectorProyecto && (
        <div className="modal-overlay" onClick={() => setMostrarSelectorProyecto(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Seleccionar Proyecto</h2>
              <button 
                onClick={() => setMostrarSelectorProyecto(false)}
                className="btn-close"
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body">
              <p className="modal-description">
                ¿Para qué proyecto deseas crear la certificación?
              </p>
              
              <div className="proyectos-selector-grid">
                {proyectos.map(proyecto => (
                  <div
                    key={proyecto.id}
                    onClick={() => handleSeleccionarProyecto(proyecto.id)}
                    className="proyecto-selector-card"
                  >
                    <div className="proyecto-icon">🏗️</div>
                    <div className="proyecto-info">
                      <h3>{proyecto.nombre}</h3>
                      <p>{proyecto.ubicacion}</p>
                    </div>
                    <span className="proyecto-arrow">→</span>
                  </div>
                ))}
              </div>
              
              {proyectos.length === 0 && (
                <div className="empty-proyectos">
                  <p>No hay proyectos activos</p>
                  <button 
                    onClick={() => navigate('/proyectos')}
                    className="btn-secondary"
                  >
                    Ir a Proyectos
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificacionesGlobal;